import { useCallback, useEffect, useMemo, useState } from 'react'
import { WelcomeHeader } from '@/components/features/home-tab-sections/welcome-header'
import { RecentActivitySection } from '@/components/features/home-tab-sections/recent-activity-section'
import { HomeInboxFeedSection } from '@/components/features/home-tab-sections/home-inbox-feed-section'
import { HomeRecentChatsFeedSection } from '@/components/features/home-tab-sections/home-recent-chats-feed-section'
import { HomeFlowsFeedSection } from '@/components/features/home-tab-sections/home-flows-feed-section'
import { BasecampBookmarksSection } from '@/components/features/home-tab-sections/basecamp-bookmarks-section'
import { BasecampProjectsSection } from '@/components/features/home-tab-sections/basecamp-projects-section'
import {
  QuickActionsSection,
  type WorkspaceBrowseTarget,
} from '@/components/features/home-tab-sections/quick-actions-section'
import { useBasecampAuth } from '@/hooks/use-basecamp'
import { useWorkspaceExtensions } from '@/hooks/use-workspace-extensions'
import { useWorkspace } from '@/providers/workspace-provider'
import { useSpacesForWorkspaces } from '@/hooks/use-spaces'
import { documentsRootPathForWorkspace } from '@/lib/workspace-documents-root'
import { WidgetGrid } from './widget-grid'
import {
  GLOBAL_HOME_LAYOUT,
  GLOBAL_HOME_BASECAMP_BOOKMARKS,
  GLOBAL_HOME_BASECAMP_PROJECTS,
} from './global-home-layout'
import type { GlobalHomeWidgetLayoutEntry, WidgetDefinition } from './widget-types'
import type { InboxItem } from '@/hooks/use-inbox'
import type { Flow } from '@/lib/api'
import { HomeChromeBar } from '@/components/dashboard/home-chrome-bar'
import { HomePreviewPane } from '@/components/dashboard/home-preview-pane'
import {
  documentIdFromHomeSelection,
  homeSelectionKey,
  readHomePreviewOpen,
  writeHomePreviewOpen,
  type HomeSelection,
} from '@/components/dashboard/home-selection'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { IndexSurfaceShell, SecondaryPreviewResizeHandle } from '@/components/integrations/_shared'
import { isModClick } from '@/lib/open-intent'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export interface GlobalHomePageProps {
  onDocumentClick?: (documentId: string, event?: React.MouseEvent) => void
  onNewDocument?: () => void
  onSearch?: () => void
  onBrowseWorkspace?: (documentsRootPath: string) => void
  onInboxItemActivate?: (item: InboxItem) => void
  onChatThreadClick?: (threadId: string, title: string, event?: React.MouseEvent) => void
  onFlowClick?: (flow: Flow) => void
}

function isExtensionExplicitlyEnabled(
  extensions: { extension_id: string; enabled: boolean }[],
  extensionId: string,
  isLoading: boolean,
): boolean {
  if (isLoading) return false
  const ext = extensions.find((e) => e.extension_id === extensionId)
  return ext?.enabled === true
}

function openBasecampTodo(
  selection: Extract<HomeSelection, { kind: 'basecamp-todo' }>,
  event?: React.MouseEvent,
) {
  const bookmark = selection.assignment
  const projectId = bookmark.project?.id || bookmark.project_id || bookmark.bucket_id
  if (!projectId) {
    console.error('[GlobalHomePage] Basecamp todo missing project ID:', bookmark)
    return
  }
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-todo-${bookmark.id}`,
        title: bookmark.title,
        type: 'integration-basecamp-todo',
        openInNewTab: isModClick(event),
        integrationData: {
          integrationId: 'basecamp',
          projectId: projectId.toString(),
          todoId: bookmark.id.toString(),
          projectName: bookmark.project?.name || bookmark.project_name || 'Unknown Project',
        },
      },
    }),
  )
}

function openBasecampProject(selection: Extract<HomeSelection, { kind: 'basecamp-project' }>) {
  const project = selection.project
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-project-${project.id}`,
        title: project.name || 'Basecamp Project',
        type: 'integration-basecamp',
        integrationData: {
          integrationId: 'basecamp',
          projectId: project.id.toString(),
        },
      },
    }),
  )
}

export function GlobalHomePage({
  onDocumentClick,
  onNewDocument,
  onSearch,
  onBrowseWorkspace,
  onInboxItemActivate,
  onChatThreadClick,
  onFlowClick,
}: GlobalHomePageProps) {
  const { authStatus, isLoading: isBasecampLoading } = useBasecampAuth()
  const { activeWorkspaces, activeWorkspaceIds, workspaces } = useWorkspace()
  const primaryOrgId = activeWorkspaces[0]?.id ?? null
  const { extensions, isLoading: extLoading } = useWorkspaceExtensions(primaryOrgId)
  const { spacesByOrg } = useSpacesForWorkspaces(activeWorkspaceIds)

  const [selection, setSelection] = useState<HomeSelection | null>(null)
  const [previewOpen, setPreviewOpen] = useState(() => readHomePreviewOpen())

  useEffect(() => {
    const main = document.querySelector(`[data-testid="${testIds.resizableLayout.mainArea}"]`)
    if (!(main instanceof HTMLElement)) return
    if (previewOpen) main.dataset.homePreviewOpen = 'true'
    else delete main.dataset.homePreviewOpen
    return () => {
      delete main.dataset.homePreviewOpen
    }
  }, [previewOpen])

  const showWorkspaceRails = activeWorkspaceIds.length > 1

  const togglePreview = useCallback(() => {
    setPreviewOpen((prev) => {
      const next = !prev
      writeHomePreviewOpen(next)
      return next
    })
  }, [])

  const selectHome = useCallback((next: HomeSelection, opts?: { openPreview?: boolean; flow?: Flow }) => {
    setSelection(next)
    if (opts?.openPreview) {
      setPreviewOpen(true)
      writeHomePreviewOpen(true)
    }
  }, [])

  const openSelection = useCallback(
    (sel: HomeSelection | null, event?: React.MouseEvent) => {
      if (!sel) return
      switch (sel.kind) {
        case 'document':
          onDocumentClick?.(sel.id, event)
          break
        case 'suggestion':
          if (sel.documentId) {
            onDocumentClick?.(sel.documentId, event)
          } else if (sel.actionType === 'open_todo' && primaryOrgId) {
            window.dispatchEvent(
              new CustomEvent('integration-view-change', {
                detail: {
                  tabId: `basecamp-todo-${sel.actionId}`,
                  title: sel.title,
                  type: 'integration-basecamp-todo',
                  workspaceId: primaryOrgId,
                  openInNewTab: isModClick(event),
                  integrationData: {
                    integrationId: 'basecamp',
                    todoId: sel.actionId,
                    organizationId: primaryOrgId,
                  },
                },
              }),
            )
          }
          break
        case 'inbox':
          onInboxItemActivate?.(sel.item)
          break
        case 'chat':
          onChatThreadClick?.(sel.threadId, sel.title, event)
          break
        case 'flow':
          if (sel.path) {
            const summary = sel.summary
            onFlowClick?.({
              id: sel.id,
              title: sel.title,
              slug: '',
              file_path: sel.path,
              enabled: summary?.enabled ?? true,
              paused: summary?.paused ?? false,
              paused_reason: summary?.pausedReason ?? null,
              last_run_at: summary?.lastRunAt ?? null,
              last_run_status: summary?.lastRunStatus ?? null,
              triggers: summary?.triggers ?? [],
              tags: summary?.tags ?? [],
              health: summary?.health,
            })
          }
          break
        case 'basecamp-todo':
          openBasecampTodo(sel, event)
          break
        case 'basecamp-project':
          openBasecampProject(sel)
          break
      }
    },
    [
      onChatThreadClick,
      onDocumentClick,
      onFlowClick,
      onInboxItemActivate,
      primaryOrgId,
    ],
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !selection) return
      const target = e.target as HTMLElement | null
      if (target?.closest('input, textarea, [contenteditable="true"]')) return
      openSelection(selection)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openSelection, selection])

  const showBasecamp = useMemo(
    () =>
      !isBasecampLoading &&
      authStatus?.authenticated === true &&
      isExtensionExplicitlyEnabled(extensions, 'basecamp', extLoading),
    [authStatus?.authenticated, extensions, extLoading, isBasecampLoading],
  )

  const browseTargets = useMemo((): WorkspaceBrowseTarget[] => {
    return activeWorkspaces.map((ws) => ({
      workspaceId: ws.id,
      name: ws.name,
      documentsRootPath: documentsRootPathForWorkspace(ws, spacesByOrg[ws.id] ?? []),
    }))
  }, [activeWorkspaces, spacesByOrg])

  const selectedKey = homeSelectionKey(selection)

  const continuity = useMemo(
    () => ({
      selectedKey,
      selection,
      previewOpen,
      showWorkspaceRails,
      selectHome,
      openSelection,
      workspaces,
    }),
    [
      openSelection,
      previewOpen,
      selectHome,
      selectedKey,
      selection,
      showWorkspaceRails,
      workspaces,
    ],
  )

  const widgetDefinitions = useMemo(() => {
    const defs = new Map<GlobalHomeWidgetLayoutEntry['type'], WidgetDefinition>()
    defs.set('welcome-header', {
      type: 'welcome-header',
      render: () => <WelcomeHeader activeWorkspaceIds={activeWorkspaceIds} />,
    })
    defs.set('recent-activity', {
      type: 'recent-activity',
      render: () => (
        <RecentActivitySection
          onDocumentClick={onDocumentClick}
          activeWorkspaceIds={activeWorkspaceIds}
          workspaces={workspaces}
          continuity={continuity}
        />
      ),
    })
    defs.set('inbox-feed', {
      type: 'inbox-feed',
      render: () => (
        <HomeInboxFeedSection
          onInboxItemActivate={onInboxItemActivate}
          continuity={continuity}
        />
      ),
    })
    defs.set('recent-chats-feed', {
      type: 'recent-chats-feed',
      render: () => (
        <HomeRecentChatsFeedSection
          onChatThreadClick={onChatThreadClick}
          continuity={continuity}
        />
      ),
    })
    defs.set('flows-feed', {
      type: 'flows-feed',
      render: () => (
        <HomeFlowsFeedSection onFlowClick={onFlowClick} continuity={continuity} />
      ),
    })
    defs.set('quick-actions', {
      type: 'quick-actions',
      render: () => (
        <QuickActionsSection
          onNewDocument={onNewDocument}
          onSearch={onSearch}
          browseTargets={browseTargets}
          onBrowseWorkspace={onBrowseWorkspace}
        />
      ),
    })
    defs.set('basecamp-bookmarks', {
      type: 'basecamp-bookmarks',
      render: () => <BasecampBookmarksSection continuity={continuity} />,
    })
    defs.set('basecamp-projects', {
      type: 'basecamp-projects',
      render: () => <BasecampProjectsSection continuity={continuity} />,
    })
    return defs
  }, [
    activeWorkspaceIds,
    browseTargets,
    continuity,
    onBrowseWorkspace,
    onChatThreadClick,
    onDocumentClick,
    onFlowClick,
    onInboxItemActivate,
    onNewDocument,
    onSearch,
    workspaces,
  ])

  const layout = useMemo(() => {
    const entries: GlobalHomeWidgetLayoutEntry[] = [...GLOBAL_HOME_LAYOUT]
    if (showBasecamp) {
      const recentIdx = entries.findIndex((e) => e.type === 'recent-activity')
      if (recentIdx >= 0) {
        entries.splice(recentIdx + 1, 0, GLOBAL_HOME_BASECAMP_BOOKMARKS)
      }
      entries.push(GLOBAL_HOME_BASECAMP_PROJECTS)
    }
    return entries
  }, [showBasecamp])

  return (
    <div className={cn('flex h-full min-h-0 w-full', previewOpen && 'flex-row')}>
      {previewOpen ? (
        <style>{`
          [data-home-preview-open="true"] [data-testid="${testIds.documentViewer.tabList}"] {
            padding-right: min(380px, 40%);
          }
        `}</style>
      ) : null}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <IndexSurfaceShell
          header={
            <HomeChromeBar
              previewOpen={previewOpen}
              onTogglePreview={togglePreview}
            />
          }
          footer={
            <ListSurfaceStatusBar
              includePreviewTip
              data-testid={testIds.home.statusBar}
              helpTestId={testIds.home.statusBarHelp}
            />
          }
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="@container/home-feed mx-auto min-w-0 max-w-7xl p-4 sm:p-6">
              <WidgetGrid layout={layout} definitions={widgetDefinitions} />
            </div>
          </div>
        </IndexSurfaceShell>
      </div>
      {previewOpen ? (
        <>
          <SecondaryPreviewResizeHandle />
          <aside
            className="z-30 -mt-10 flex h-[calc(100%+2.5rem)] min-h-0 w-[min(380px,40%)] shrink-0 flex-col overflow-hidden border-l border-border bg-background"
            aria-label="Home preview"
            data-testid={testIds.home.previewPane}
          >
            <HomePreviewPane
              selection={selection}
              onOpen={(event) => openSelection(selection, event)}
              onClose={() => {
                setPreviewOpen(false)
                writeHomePreviewOpen(false)
              }}
            />
          </aside>
        </>
      ) : null}
    </div>
  )
}

export type { HomeSelection }
export { documentIdFromHomeSelection, homeSelectionKey }
